import type { StaffSessionTokensResponseDto } from '@idc-forms/api-client'
import * as Sentry from '@sentry/react'
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import type { IUserAuth } from '@/features/users/models/user'
import { registerV2SessionRecovery } from '@/shared/api/v2-session-fetch'
import { staffSessionV2Service } from '../staff-session-v2.service'

/**
 * Cuánto antes del vencimiento se renueva el access token. Con un TTL de 15 minutos, refresca
 * a los ~13: suficiente margen para una red lenta, y lejos de convertirse en tráfico de fondo.
 */
const REFRESH_SKEW_MS = 2 * 60 * 1000

/**
 * Piso del temporizador. Existe para el caso en que el token YA está vencido al programarlo
 * (la laptop durmió y `setTimeout` se atrasó): sin piso, `delay` sería 0 o negativo y el
 * refresh saldría en el mismo tick del render.
 */
const MIN_REFRESH_DELAY_MS = 30 * 1000

/**
 * Ruido aleatorio que se le suma al temporizador, y NO es un detalle de afinación: es la
 * defensa contra un logout masivo.
 *
 * El refresh token ROTA, y el backend detecta reuso sin perdón — dos refresh concurrentes con
 * la misma cookie hacen que el segundo caiga en `usedRefreshTokenHashes` (o falle el update
 * condicional) y `refreshStaff` responde revocando la FAMILIA entera de sesiones: te saca de
 * todas las pestañas de una. Dos pestañas abiertas en el mismo minuto tendrían un `expiresAt`
 * casi idéntico y sus temporizadores dispararían con milisegundos de diferencia, que es
 * exactamente la carrera que hay que evitar. El desfasaje las separa.
 */
const REFRESH_JITTER_MS = 60 * 1000

export interface AuthContextType {
  auth: IUserAuth | null
  setAuth: Dispatch<SetStateAction<IUserAuth | null>>
  persist: boolean
  setPersist: Dispatch<SetStateAction<boolean>>
  completeV2Session: (session: StaffSessionTokensResponseDto) => Promise<IUserAuth>
  refreshV2Session: () => Promise<string | null>
  logoutV2Session: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType>({
  auth: null,
  setAuth: () => null,
  persist: false,
  setPersist: () => false,
  completeV2Session: async () => await Promise.reject(new Error('AuthProvider is not mounted')),
  refreshV2Session: async () => null,
  logoutV2Session: async () => undefined
})

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [auth, setAuth] = useState<IUserAuth | null>(null)
  // 'persist' is never written to localStorage (the login write is commented
  // out) and the old `JSON.parse(...) || true` collapsed to true for every
  // possible stored value, so the initial state is a plain constant.
  const [persist, setPersist] = useState<boolean>(true)
  /**
   * La renovación en curso, deduplicada, guardando la SESIÓN ENTERA y no un token suelto.
   *
   * Hay dos consumidores que necesitan credenciales DISTINTAS de la misma renovación: el
   * interceptor de axios reintenta llamadas de la API v1 y precisa el token legacy
   * (`accessToken`), mientras que el transporte v2 precisa `v2AccessToken`. Antes esto
   * guardaba `Promise<string>` con el legacy —correcto para su único consumidor de entonces—
   * y devolverle ESE token a un reintento de la v2 lo hace fallar con otro 401.
   */
  const refreshInFlight = useRef<Promise<IUserAuth> | null>(null)
  /**
   * Cuándo vence el access token vigente, en epoch ms. Es un ref y no estado porque nadie lo
   * RENDERIZA: sólo lo leen el temporizador y el chequeo al volver el foco.
   */
  const expiresAt = useRef<number | null>(null)

  /**
   * La sesión ya se dio por perdida: un refresh falló y no hay ninguno nuevo que probar.
   *
   * ☠️ SIN ESTO, `refreshInFlight` DEDUPLICA LA RÁFAGA PERO NO LA CASCADA — Y SON DISTINTAS.
   *
   * `refreshInFlight` sólo agrupa lo que llega MIENTRAS la renovación está en vuelo. Cuando
   * falla, su `finally` lo limpia, así que el siguiente `401` —que llega más tarde, porque las
   * respuestas no vuelven todas juntas— arranca OTRA renovación, que vuelve a fallar, que
   * vuelve a limpiar. Medido en producción el 2026-08-14: tres `POST /auth/staff/refresh` en
   * dos segundos, detrás de tres listados que habían fallado con `401`.
   *
   * Y no es sólo ruido. El backend detecta REUSO del refresh token sin perdón: un token que ya
   * fue usado revoca la FAMILIA ENTERA de sesiones. Cada reintento de más es una oportunidad de
   * que una carrera —dos pestañas comparten la cookie, que es del navegador y no del árbol de
   * React— convierta un token vencido en un logout de todas.
   *
   * El latch se levanta solo en `completeV2Session`, o sea cuando VUELVE a haber sesión: un
   * login nuevo, o una renovación que sí funcionó.
   */
  const sessionLost = useRef(false)

  const completeV2Session = useCallback(async (session: StaffSessionTokensResponseDto) => {
    const nextAuth = await staffSessionV2Service.hydrate(session)
    expiresAt.current = Date.now() + session.expiresInSeconds * 1000
    sessionLost.current = false
    setAuth(nextAuth)
    return nextAuth
  }, [])

  /** Renueva la sesión una sola vez por ráfaga y devuelve la sesión completa. */
  const refreshSession = useCallback(() => {
    if (sessionLost.current) {
      return Promise.reject(new Error('La sesión expiró. Vuelve a iniciar sesión.'))
    }
    if (refreshInFlight.current) return refreshInFlight.current

    const pending = staffSessionV2Service
      .refresh()
      .then(completeV2Session)
      .catch((error) => {
        sessionLost.current = true
        throw error
      })
      .finally(() => {
        if (refreshInFlight.current === pending) refreshInFlight.current = null
      })

    refreshInFlight.current = pending
    return pending
  }, [completeV2Session])

  /**
   * Devuelve el token LEGACY, y así tiene que quedarse: lo consume `useRefreshToken` para que
   * el interceptor de axios reintente llamadas de la API **v1**. El transporte v2 usa
   * `v2AccessToken`, que se saca de la misma renovación deduplicada más abajo.
   */
  const refreshV2Session = useCallback(
    async () => (await refreshSession()).accessToken,
    [refreshSession]
  )

  /**
   * Cierra la sesión v2. **Nunca rechaza**, y eso es lo que define su contrato.
   *
   * Cerrar sesión es IDEMPOTENTE: el objetivo es quedarse sin sesión, así que un `401` del
   * servidor —el token ya venció, o la sesión se revocó desde otro lado— significa que el
   * objetivo YA está cumplido. Propagarlo convierte un final feliz en un error.
   *
   * Antes esto era `try/finally` sin `catch`: el `finally` limpiaba la sesión pero el rechazo
   * seguía viaje, y como `useLogout` tampoco lo atrapaba terminaba en un `UnhandledRejection`.
   * El SDK v2 corre con `throwOnError: true`, así que lo que tira es el cuerpo RFC 7807 crudo
   * —un objeto plano, no un `Error`—, y de ahí el `"Object captured as promise rejection with
   * keys: code, detail, instance, requestId, status, title, type"` de Sentry, que no dice
   * dónde se originó. Además cortaba el `await logout()` de `NavUser`, así que el `navigate`
   * al login de la línea siguiente nunca corría.
   *
   * Se registra como `warning` en vez de descartarse en silencio: que el servidor no llegue a
   * cerrar la sesión es un dato —una revocación que no llegó deja el refresh token vivo del
   * otro lado— pero no es algo que quien cierra sesión pueda accionar.
   */
  const logoutV2Session = useCallback(async () => {
    const accessToken = auth?.v2AccessToken
    try {
      if (accessToken) await staffSessionV2Service.logout(accessToken)
    } catch (error) {
      Sentry.captureMessage('v2 logout failed; the local session is cleared anyway', {
        level: 'warning',
        extra: { cause: error }
      })
    } finally {
      refreshInFlight.current = null
      // Quien cierra sesión a propósito no tiene una sesión perdida: la próxima entrada es un
      // login limpio, y dejar el latch puesto haría que su primer refresh se rechace sin probar.
      sessionLost.current = false
      expiresAt.current = null
      setAuth(null)
    }
  }, [auth?.v2AccessToken])

  /**
   * Le presta el refresh al transporte v2, que necesita renovar la sesión desde fuera del árbol
   * de React (ver `v2-session-fetch.ts`). `refreshSession` deduplica en DOS tiempos: por
   * `refreshInFlight` mientras la renovación está en vuelo —los ocho listados que fallan juntos
   * al volver a una pestaña vieja disparan UN refresh— y por `sessionLost` una vez que falló,
   * que es lo que corta la cascada de reintentos tardíos.
   */
  useEffect(() => {
    registerV2SessionRecovery({
      // `v2AccessToken` y NO `accessToken`: el reintento va contra la API v2, que rechaza el
      // token legacy con otro 401 — el fallo se ve como «el refresh no sirvió» y manda a
      // buscar el problema al lugar equivocado.
      refresh: async () => (await refreshSession()).v2AccessToken ?? null,
      onSessionLost: () => {
        refreshInFlight.current = null
        expiresAt.current = null
        // No navega: `RequireAuth` ya manda al login sin sesión, y preservando la ruta de origen.
        setAuth(null)
      }
    })
    return () => registerV2SessionRecovery(null)
  }, [refreshSession])

  /**
   * Renovación proactiva: que el 401 ni siquiera ocurra mientras la pestaña esté viva.
   *
   * Se reprograma sola sin recursión explícita: cada refresh exitoso produce un `auth` nuevo,
   * que vuelve a correr este efecto, que agenda el siguiente. Si el refresh falla, `auth` no
   * cambia y la cadena se corta — a propósito: ahí manda la recuperación reactiva del 401, que
   * es la que sabe distinguir un tropiezo de red de una sesión muerta.
   */
  useEffect(() => {
    if (!auth) return
    let timer: number | undefined

    const scheduleNext = () => {
      if (expiresAt.current === null) return
      const delay =
        Math.max(expiresAt.current - Date.now() - REFRESH_SKEW_MS, MIN_REFRESH_DELAY_MS) +
        Math.random() * REFRESH_JITTER_MS

      timer = window.setTimeout(() => {
        // Sólo renueva la pestaña que se está MIRANDO. Dos motivos, y el segundo es el que
        // manda: una pestaña de fondo no tiene ninguna urgencia por un token fresco, y sobre
        // todo cada pestaña que renueva es otra candidata a la carrera de rotación que revoca
        // la familia de sesiones. Vuelve a agendar en vez de rendirse: si se abandonara acá,
        // una pestaña que se oculta justo en ese instante y se vuelve a mirar ANTES del
        // vencimiento se quedaría sin temporizador y sin chequeo de foco que la despierte.
        if (document.visibilityState !== 'visible') {
          scheduleNext()
          return
        }
        void refreshSession().catch(() => {
          // Silencioso a propósito: es una renovación de fondo que el usuario no pidió. Si de
          // verdad quedó sin sesión, se entera por la primera request que haga, no por un toast
          // que aparece solo mientras mira otra cosa.
        })
      }, delay)
    }

    scheduleNext()
    return () => {
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [auth, refreshSession])

  /**
   * Y esta es la que cubre el caso real que se reportó: `setTimeout` se atrasa —o directamente
   * no corre— cuando la máquina duerme o el navegador congela una pestaña de fondo. Al volver
   * el foco no se confía en el temporizador: se mira el reloj.
   */
  useEffect(() => {
    if (!auth) return

    const revalidateOnFocus = () => {
      if (document.visibilityState !== 'visible') return
      if (expiresAt.current === null) return
      if (Date.now() < expiresAt.current - REFRESH_SKEW_MS) return
      void refreshSession().catch(() => {})
    }

    document.addEventListener('visibilitychange', revalidateOnFocus)
    return () => document.removeEventListener('visibilitychange', revalidateOnFocus)
  }, [auth, refreshSession])

  useEffect(() => {
    if (auth) {
      Sentry.setUser({ id: auth.publicId ?? auth._id, username: auth.username })
    } else {
      Sentry.setUser(null)
    }
  }, [auth])

  const value = useMemo(
    () => ({
      auth,
      setAuth,
      persist,
      setPersist,
      completeV2Session,
      refreshV2Session,
      logoutV2Session
    }),
    [auth, completeV2Session, logoutV2Session, persist, refreshV2Session]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider
